import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Globe,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Star,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { sitesApi } from '../api/client';
import { getPublishedSiteUrl, getPublishedDomain } from '../utils/publishedUrl';
import type { Site } from '../schemas/validation';
import { cn } from '../utils/cn';
import { DnsInstructions } from './SiteSettings/DnsInstructions';
import { FaviconUploadModal } from '../components/FaviconUploadModal';
import { ConfirmModal } from '../components/ConfirmModal';

// Check if we're in mimic mode (for layout offset)
const useIsMimicking = () => {
  const [isMimicking, setIsMimicking] = useState(false);

  useEffect(() => {
    const checkMimicState = () => {
      const originalToken = localStorage.getItem('originalAdminToken');
      const mimickedUserData = localStorage.getItem('mimickedUser');
      setIsMimicking(!!originalToken && !!mimickedUserData);
    };

    checkMimicState();
    window.addEventListener('storage', checkMimicState);
    return () => window.removeEventListener('storage', checkMimicState);
  }, []);

  return isMimicking;
};

export const SiteSettings: React.FC = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const isMimicking = useIsMimicking();

  const [site, setSite] = useState<Site | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDnsInstructions, setShowDnsInstructions] = useState(false);

  // Form fields
  const [siteName, setSiteName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [domainError, setDomainError] = useState<string | null>(null);

  // Favicon state
  const [showFaviconModal, setShowFaviconModal] = useState(false);
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);

  // Domain verification state
  const [isVerifyingDomain, setIsVerifyingDomain] = useState(false);
  const [domainVerification, setDomainVerification] = useState<{
    dnsConfigured: boolean;
    aRecord: boolean;
    cnameRecord: boolean;
    sslActive: boolean;
    errors: string[];
  } | null>(null);

  // DNS records (fetched live from Vercel via API)
  const [dnsRecords, setDnsRecords] = useState<Array<{
    type: string;
    name: string;
    value: string;
    description?: string;
  }> | null>(null);
  const [dnsRecordsLoading, setDnsRecordsLoading] = useState(false);
  const [dnsRecordsError, setDnsRecordsError] = useState<string | null>(null);
  const [dnsUsingFallback, setDnsUsingFallback] = useState(false);

  // Remove-custom-domain confirmation modal
  const [showRemoveDomainModal, setShowRemoveDomainModal] = useState(false);
  const [isRemovingDomain, setIsRemovingDomain] = useState(false);

  useEffect(() => {
    if (!siteId) {
      navigate('/dashboard');
      return;
    }
    loadSite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, navigate]);

  const loadSite = async () => {
    try {
      setIsLoading(true);
      const sites = await sitesApi.list();
      const currentSite = sites.find(s => s.id === siteId);

      if (!currentSite) {
        navigate('/dashboard');
        return;
      }

      setSite(currentSite);
      setSiteName(currentSite.name);
      setSubdomain(currentSite.subdomain || '');
      setCustomDomain(currentSite.customDomain || '');
      setFaviconUrl(currentSite.faviconUrl || null);

      // Show DNS instructions if domain is set but not active
      if (currentSite.customDomain) {
        setShowDnsInstructions(true);
        void fetchDnsRecords(currentSite.id);
      }
    } catch (error) {
      console.error('Failed to load site:', error);
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDnsRecords = async (id: string) => {
    try {
      setDnsRecordsLoading(true);
      setDnsRecordsError(null);
      const response = await sitesApi.getDnsRecords(id);
      setDnsRecords(response.records);
      setDnsUsingFallback(response.usingFallback);
    } catch (error: any) {
      console.error('Failed to fetch DNS records:', error);
      setDnsRecords(null);
      setDnsRecordsError(
        error?.response?.data?.error ||
          error?.message ||
          'Could not load DNS recommendations from Vercel.'
      );
    } finally {
      setDnsRecordsLoading(false);
    }
  };

  const confirmRemoveDomain = async () => {
    if (!site || !siteId || !site.customDomain) return;

    try {
      setIsRemovingDomain(true);
      setError(null);
      const updatedSite = await sitesApi.update(siteId, {
        name: siteName,
        customDomain: null,
      });
      setSite(updatedSite);
      setCustomDomain('');
      setShowDnsInstructions(false);
      setDnsRecords(null);
      setDnsRecordsError(null);
      setDnsUsingFallback(false);
      setDomainVerification(null);
      setShowRemoveDomainModal(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error: any) {
      console.error('Failed to remove custom domain:', error);
      setError(
        error?.response?.data?.message || error?.message || 'Failed to remove custom domain'
      );
    } finally {
      setIsRemovingDomain(false);
    }
  };

  const validateCustomDomain = (domain: string): boolean => {
    if (!domain) return true; // Optional field

    // Remove protocol and www
    const cleaned = domain
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '');

    // Basic domain validation
    const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;

    if (!domainRegex.test(cleaned)) {
      setDomainError('Please enter a valid domain (e.g., example.com)');
      return false;
    }

    setDomainError(null);
    return true;
  };

  const cleanDomain = (domain: string): string | null => {
    if (!domain || !domain.trim()) return null;

    const cleaned = domain
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .trim();

    // Return null if the cleaned domain is empty
    return cleaned === '' ? null : cleaned;
  };

  const handleSave = async () => {
    if (!site || !siteId) return;

    // Validate domain if provided
    if (customDomain && !validateCustomDomain(customDomain)) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const cleanedDomain = cleanDomain(customDomain);

      // Build update payload - send empty string to remove domain
      const updatePayload: any = {
        name: siteName,
        customDomain: cleanedDomain === null ? '' : cleanedDomain,
      };

      console.log('Saving site settings:', {
        siteName,
        customDomain: customDomain,
        cleanedDomain: cleanedDomain,
        isNull: cleanedDomain === null,
        sendingEmptyString: cleanedDomain === null,
        updatePayload,
      });

      const updatedSite = await sitesApi.update(siteId, updatePayload);

      console.log('Update response site data:', updatedSite);

      setSite(updatedSite);
      setCustomDomain(updatedSite.customDomain || '');
      setShowSuccess(true);

      // Show DNS instructions if domain was added/changed
      if (cleanedDomain && cleanedDomain !== site.customDomain) {
        setShowDnsInstructions(true);
        if (siteId) void fetchDnsRecords(siteId);
      } else if (!cleanedDomain && site.customDomain) {
        // Domain was removed
        setShowDnsInstructions(false);
      }

      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error: any) {
      console.error('Failed to update site:', error);
      console.error('Error data:', error.data);
      console.error('Error details:', error.data?.details);
      if (error.status === 409) {
        setError('This domain is already in use by another site');
      } else if (
        error.data?.details &&
        Array.isArray(error.data.details) &&
        error.data.details.length > 0
      ) {
        // Show the first validation error detail
        const firstDetail = error.data.details[0];
        setError(firstDetail.message || firstDetail.error || JSON.stringify(firstDetail));
      } else if (error.data?.message) {
        setError(error.data.message);
      } else if (error.data?.error) {
        setError(error.data.error);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError('Failed to update site settings. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
  };

  const handleFaviconUploaded = async (newFaviconUrl: string) => {
    if (!siteId) return;

    try {
      const updatedSite = await sitesApi.update(siteId, { faviconUrl: newFaviconUrl || null });
      setSite(updatedSite);
      setFaviconUrl(newFaviconUrl || null);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update favicon:', err);
      setError('Failed to update favicon. Please try again.');
    }
  };

  const handleRemoveFavicon = async () => {
    await handleFaviconUploaded('');
  };

  const handleVerifyDomain = async () => {
    if (!siteId || !site?.customDomain) return;

    try {
      setIsVerifyingDomain(true);
      setError(null);
      const result = await sitesApi.verifyDomain(siteId);
      setDomainVerification({
        dnsConfigured: result.dnsConfigured,
        aRecord: result.aRecord,
        cnameRecord: result.cnameRecord,
        sslActive: result.sslActive,
        errors: result.errors,
      });
    } catch (err) {
      console.error('Failed to verify domain:', err);
      setError('Failed to verify domain configuration. Please try again.');
    } finally {
      setIsVerifyingDomain(false);
    }
  };

  const getDomainStatus = () => {
    if (!domainVerification) {
      return { color: 'bg-gray-400', text: 'Not Verified', textColor: 'text-gray-600' };
    }
    if (domainVerification.sslActive) {
      return { color: 'bg-green-500', text: 'Active', textColor: 'text-green-600' };
    }
    if (domainVerification.dnsConfigured) {
      return {
        color: 'bg-yellow-500',
        text: 'DNS Configured - SSL Pending',
        textColor: 'text-yellow-600',
      };
    }
    return { color: 'bg-red-500', text: 'DNS Not Configured', textColor: 'text-red-600' };
  };

  const getWebsiteUrl = () => {
    if (!site) return '';
    return getPublishedSiteUrl(site);
  };

  if (isLoading) {
    return (
      <div
        className={`page-container flex items-center justify-center ${isMimicking ? 'mt-[52px]' : ''}`}
      >
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 spinner"></div>
          <p className="text-gray-600 font-medium">Loading settings...</p>
        </div>
      </div>
    );
  }

  if (!site) {
    return (
      <div
        className={`page-container flex items-center justify-center ${isMimicking ? 'mt-[52px]' : ''}`}
      >
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Site not found</h2>
          <button onClick={() => navigate('/dashboard')} className="mt-4 btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`page-container ${isMimicking ? 'mt-[52px]' : ''}`}>
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/sites/${siteId}`)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft size={20} />
                <span>Back to Site</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
            </div>

            <button
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary inline-flex items-center space-x-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 spinner"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={20} />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Success Message */}
        {showSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
            <div>
              <p className="text-green-800 font-medium">Settings saved successfully!</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* General Settings */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">General Settings</h2>

          <div className="space-y-6">
            {/* Site Name */}
            <div>
              <label htmlFor="siteName" className="block text-sm font-medium text-gray-700 mb-2">
                Site Name
              </label>
              <input
                type="text"
                id="siteName"
                value={siteName}
                onChange={e => setSiteName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="My Amazing Website"
              />
            </div>

            {/* Subdomain (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subdomain</label>
              <p className="text-sm text-gray-900 font-mono">
                {subdomain}
                <span className="text-gray-600">.{getPublishedDomain()}</span>
              </p>
              <p className="mt-1 text-sm text-gray-500">
                This is your default URL and cannot be changed
              </p>
            </div>
          </div>
        </div>

        {/* Custom Domain Settings */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Custom Domain</h2>
            <Globe className="h-5 w-5 text-gray-400" />
          </div>

          <div className="space-y-6">
            {/* Custom Domain Input */}
            <div>
              <label
                htmlFor="customDomain"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Custom Domain (Optional)
              </label>
              <input
                type="text"
                id="customDomain"
                value={customDomain}
                onChange={e => {
                  setCustomDomain(e.target.value);
                  setDomainError(null);
                }}
                onBlur={() => validateCustomDomain(customDomain)}
                className={cn(
                  'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500',
                  domainError ? 'border-red-300' : 'border-gray-300'
                )}
                placeholder="yourdomain.com"
              />
              {domainError && <p className="mt-1 text-sm text-red-600">{domainError}</p>}
              <p className="mt-1 text-sm text-gray-500">
                Enter your domain without https:// or www. Example: mybusiness.com
              </p>
              {site?.customDomain && (
                <button
                  type="button"
                  onClick={() => setShowRemoveDomainModal(true)}
                  disabled={isSaving || isRemovingDomain}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm text-red-700 border border-red-300 rounded-md hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  Remove Custom Domain
                </button>
              )}
            </div>

            {/* Current URL Display */}
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Your website will be accessible at:
              </p>
              <div className="flex items-center justify-between">
                <code className="text-sm font-mono text-primary-600">{getWebsiteUrl()}</code>
                <button
                  onClick={() => window.open(getWebsiteUrl(), '_blank')}
                  className="text-primary-600 hover:text-primary-700"
                  title="Open website"
                >
                  <ExternalLink size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* DNS Instructions */}
        {showDnsInstructions && customDomain && (
          <div className="mb-6">
            <DnsInstructions
              customDomain={customDomain}
              records={dnsRecords}
              loading={dnsRecordsLoading}
              error={dnsRecordsError}
              usingFallback={dnsUsingFallback}
              onCopyToClipboard={copyToClipboard}
            />
          </div>
        )}

        {/* Status Information */}
        {site.customDomain && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={cn('w-2 h-2 rounded-full', getDomainStatus().color)}></div>
                <span className="text-sm font-medium text-gray-700">
                  Domain Status:{' '}
                  <span className={getDomainStatus().textColor}>{getDomainStatus().text}</span>
                </span>
              </div>
              <button
                onClick={handleVerifyDomain}
                disabled={isVerifyingDomain}
                className="inline-flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
              >
                <RefreshCw size={14} className={isVerifyingDomain ? 'animate-spin' : ''} />
                <span>{isVerifyingDomain ? 'Verifying...' : 'Verify Domain'}</span>
              </button>
            </div>
            {domainVerification && domainVerification.errors.length > 0 && (
              <div className="mt-3 space-y-1">
                {domainVerification.errors.map((err, index) => (
                  <p key={index} className="text-xs text-red-600 flex items-start space-x-1">
                    <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                    <span>{err}</span>
                  </p>
                ))}
              </div>
            )}
            {domainVerification?.sslActive && (
              <p className="mt-2 text-xs text-green-600 flex items-center space-x-1">
                <CheckCircle size={12} />
                <span>SSL certificate is active</span>
              </p>
            )}
            {!domainVerification && (
              <p className="mt-2 text-xs text-gray-500">
                Click "Verify Domain" to check your DNS configuration
              </p>
            )}
          </div>
        )}

        {/* Site Branding */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Site Branding</h2>
            <Star className="h-5 w-5 text-amber-500" />
          </div>

          <div className="space-y-6">
            {/* Favicon */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Favicon</label>
              <p className="text-sm text-gray-500 mb-4">
                The small icon that appears in browser tabs and bookmarks
              </p>

              <div className="flex items-center space-x-4">
                {faviconUrl ? (
                  <div className="relative">
                    <div className="w-16 h-16 rounded-lg border-2 border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                      <img
                        src={faviconUrl}
                        alt="Current favicon"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <button
                      onClick={handleRemoveFavicon}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      title="Remove favicon"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                    <Star size={24} className="text-gray-400" />
                  </div>
                )}

                <div className="flex-1">
                  <button onClick={() => setShowFaviconModal(true)} className="btn-secondary">
                    {faviconUrl ? 'Change Favicon' : 'Upload Favicon'}
                  </button>
                  {faviconUrl && (
                    <p className="mt-2 text-xs text-gray-500">
                      Recommended size: 32x32 or 64x64 pixels
                    </p>
                  )}
                </div>
              </div>

              {/* Browser tab preview */}
              {faviconUrl && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Browser tab preview:</p>
                  <div className="inline-flex items-center space-x-2 bg-white rounded-t-lg px-3 py-2 border border-gray-300 border-b-0 shadow-sm">
                    <img src={faviconUrl} alt="Tab favicon" className="w-4 h-4 object-contain" />
                    <span className="text-xs text-gray-700 truncate max-w-[120px]">
                      {siteName || 'Your Website'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Favicon Upload Modal */}
      <FaviconUploadModal
        isOpen={showFaviconModal}
        onClose={() => setShowFaviconModal(false)}
        siteId={siteId || ''}
        currentFaviconUrl={faviconUrl || undefined}
        onFaviconUploaded={handleFaviconUploaded}
      />

      {/* Remove Custom Domain Confirmation */}
      <ConfirmModal
        isOpen={showRemoveDomainModal}
        onClose={() => !isRemovingDomain && setShowRemoveDomainModal(false)}
        onConfirm={confirmRemoveDomain}
        type="danger"
        title="Remove Custom Domain?"
        message={
          site?.customDomain
            ? `${site.customDomain} will be detached from this site and removed from Vercel. The site will only be reachable at ${subdomain}.${getPublishedDomain()} until a new domain is set.`
            : 'This will remove the custom domain from this site.'
        }
        confirmText="Remove Domain"
        cancelText="Cancel"
        isLoading={isRemovingDomain}
        loadingText="Removing..."
      />
    </div>
  );
};
